import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useReducedMotion } from 'framer-motion';
import { FiArrowDown, FiArrowUpRight, FiDownload } from 'react-icons/fi';
import SplitText from '../ui/SplitText';
import Magnetic from '../ui/Magnetic';
import { profile, heroStats } from '../../data/profile';
import portraitWebp from '../../assets/images/ayaan.webp';
import portraitJpg from '../../assets/images/ayaan.jpg';
import './Hero.css';

const ease = [0.16, 1, 0.3, 1];

export default function Hero({ ready }) {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const photoY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 120]);
  const photoScale = useTransform(scrollYProgress, [0, 1], [1, reduce ? 1 : 1.08]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -60]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const fadeUp = (delay) => ({
    initial: reduce ? false : { opacity: 0, y: 24 },
    animate: ready ? { opacity: 1, y: 0 } : {},
    transition: { duration: 0.9, delay, ease },
  });

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="hero" id="top" ref={ref}>
      <div className="hero__glow" aria-hidden />
      <div className="container hero__grid">
        <motion.div className="hero__text" style={{ y: textY, opacity: fade }}>
          <motion.p className="eyebrow" {...fadeUp(0.1)}>
            {profile.role} <span className="hero__sep">/</span> {profile.location}
          </motion.p>

          {ready && (
            <SplitText
              as="h1"
              className="display h1 hero__title"
              delay={0.2}
              stagger={0.05}
              text={[
                { text: 'I build the systems' },
                { br: true },
                { text: 'behind' },
                { text: 'AI products.', em: true },
              ]}
            />
          )}

          <motion.p className="lead hero__lead" {...fadeUp(0.75)}>
            Backend AI Engineer at <strong>AI Media Lab</strong> in Dubai, building the backend of{' '}
            <strong>Risha.ai</strong> and the platforms behind the <strong>1 Billion Followers Summit</strong> and{' '}
            <strong>Dubai Future Week</strong>.
            APIs, async pipelines, data services and LLM integrations that hold up in production.
          </motion.p>

          <motion.div className="hero__actions" {...fadeUp(0.9)}>
            <Magnetic>
              <a href="#work" className="btn btn--primary" onClick={scrollTo('work')}>
                See the work <span className="btn__icon"><FiArrowUpRight /></span>
              </a>
            </Magnetic>
            <Magnetic>
              <a href={profile.cv} className="btn btn--ghost" download="Ayaan_Ahmad_CV.pdf">
                Download CV <span className="btn__icon"><FiDownload /></span>
              </a>
            </Magnetic>
          </motion.div>

          <motion.ul className="hero__stats" {...fadeUp(1.05)}>
            {heroStats.map((s) => (
              <li key={s.label}>
                <span className="hero__stat-value">{s.value}</span>
                <span className="hero__stat-label">{s.label}</span>
              </li>
            ))}
          </motion.ul>
        </motion.div>

        <div className="hero__visual">
          <motion.div
            className="hero__photo"
            initial={reduce ? false : { clipPath: 'inset(100% 0 0 0)' }}
            animate={ready ? { clipPath: 'inset(0% 0 0 0)' } : {}}
            transition={{ duration: 1.4, delay: 0.3, ease: [0.76, 0, 0.24, 1] }}
          >
            <motion.picture style={{ y: photoY, scale: photoScale }}>
              <source srcSet={portraitWebp} type="image/webp" />
              <img src={portraitJpg} alt="Ayaan Ahmad, in profile against a dark wall" fetchpriority="high" />
            </motion.picture>
            <div className="hero__photo-fade" aria-hidden />
          </motion.div>

          <motion.a
            href="#now"
            onClick={scrollTo('now')}
            className="hero__now card"
            initial={reduce ? false : { opacity: 0, y: 30 }}
            animate={ready ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, delay: 1.3, ease }}
          >
            <span className="hero__now-label mono">
              <span className="dot-live" /> Currently
            </span>
            <span className="hero__now-title">Backend AI Engineer, AI Media Lab</span>
            <span className="hero__now-sub">Risha.ai &middot; Dubai Future Week &middot; 1 Billion Followers Summit &middot; Author of <em>Trust Issues</em></span>
            <span className="hero__now-arrow"><FiArrowUpRight /></span>
          </motion.a>
        </div>
      </div>

      <motion.a
        href="#now"
        onClick={scrollTo('now')}
        className="hero__scroll mono"
        initial={{ opacity: 0 }}
        animate={ready ? { opacity: 1 } : {}}
        transition={{ delay: 1.6, duration: 0.8 }}
        style={{ opacity: fade }}
      >
        <motion.span
          animate={reduce ? {} : { y: [0, 6, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
        >
          <FiArrowDown />
        </motion.span>
        Scroll
      </motion.a>
    </section>
  );
}
